import { ApplicationOption } from 'openfin/_v2/api/application/applicationOption';
import { injectable } from 'inversify';
import { Identity } from 'openfin/_v2/main';
import { Signal } from 'openfin-service-signal';
import { WindowEvent } from 'openfin/_v2/api/events/base';

import { mutable } from '../store/State';

import { Environment, StoredApplication, Manifest } from './Environment';
import { ManifestError } from './Errors';

@injectable()
export class FinEnvironment implements Environment {
  public readonly onWindowClosed: Signal<[Identity]> = new Signal();

  private _startingUpAppUuids: string[] = [];

  constructor() {
    fin.System.addListener('window-closed', (event: WindowEvent<'system', 'window-closed'>) => {
      const identity: Identity = { uuid: event.uuid, name: event.name };
      this.onWindowClosed.emit(identity);
    });
  }

  public async isApplicationRunning(uuid: string): Promise<boolean> {
    if (this._startingUpAppUuids.some((startingUuid) => startingUuid === uuid)) {
      return true;
    }

    return fin.Application.wrapSync({ uuid }).isRunning();
  }

  public async getApplication(uuid: string): Promise<StoredApplication | null> {
    const app = fin.Application.wrapSync({ uuid });
    const info = await app.getInfo().catch(() => null);
    const isProgrammatic = !!info?.parentUuid;

    if (!info) {
      console.warn(
        `Couldn't query application info for app '${uuid}'. Will not be able to restart this application.`
      );
      return null;
    } else if (isProgrammatic) {
      const initialOptions: Manifest = info.initialOptions;
      return {
        type: 'programmatic',
        id: uuid,
        muted: false,
        persistToasts: false,
        iconUrl:
          initialOptions.startup_app?.applicationIcon ||
          initialOptions.startup_app?.icon ||
          initialOptions.shortcut?.icon,
        title: initialOptions.name || app.identity.name || app.identity.uuid,
        initialOptions: info.initialOptions,
        parentUuid: info.parentUuid || '',
      };
    } else {
      const manifest: Manifest = info.manifest;
      return {
        type: 'manifest',
        id: uuid,
        muted: false,
        persistToasts: false,
        iconUrl:
          manifest.startup_app?.applicationIcon ||
          manifest.startup_app?.icon ||
          manifest.shortcut?.icon,
        title:
          (manifest.shortcut && manifest.shortcut.name) ||
          (manifest.startup_app && manifest.startup_app.name) ||
          app.identity.name ||
          app.identity.uuid,
        manifestUrl: info.manifestUrl,
      };
    }
  }

  public async getApplicationFromManifest(manifestUrl: string): Promise<StoredApplication> {
    const request: Response = await fetch(manifestUrl).catch((e) => {
      throw new ManifestError(`Could not request manifest from ${manifestUrl}`, e);
    });
    const manifest: Manifest = await request.json().catch((e) => {
      throw new ManifestError(`Could not parse JSON within manifest from ${manifestUrl}`, e);
    });

    const uuid: string | undefined =
      manifest.startup_app?.uuid || manifest.uuid || manifest.platform?.uuid;
    if (!uuid) {
      throw new ManifestError(`Could not find required fields (uuid/title) within ${manifestUrl}`);
    }

    return {
      type: 'manifest',
      id: uuid,
      muted: false,
      persistToasts: false,
      iconUrl:
        manifest.startup_app?.applicationIcon ||
        manifest.startup_app?.icon ||
        manifest.shortcut?.icon,
      title: manifest.shortcut?.name || manifest.startup_app?.name || uuid,
      manifestUrl,
    };
  }

  public async startApplication(application: StoredApplication): Promise<void> {
    if (this._startingUpAppUuids.some((startingUuid) => startingUuid === application.id)) {
      return;
    }

    this._startingUpAppUuids.push(application.id);

    try {
      let finApplication;
      if (application.type === 'manifest') {
        finApplication = await fin.Application.createFromManifest(application.manifestUrl);
      } else {
        finApplication = await fin.Application.create(
          mutable(application.initialOptions as ApplicationOption)
        );
      }
      await finApplication.addListener('initialized', (event) => {
        this._startingUpAppUuids = this._startingUpAppUuids.filter(
          (startingUuid) => startingUuid !== event.uuid
        );
      });
      await finApplication.run();
    } catch (error: unknown) {
      this._startingUpAppUuids = this._startingUpAppUuids.filter(
        (startingUuid) => startingUuid !== application.id
      );

      if (error instanceof Error) {
        console.error(`Error starting app ${application.id} [${error.message}]`);
      }
    }
  }
}
